import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ContactPopup from './ContactPopup';

const PricingTable = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const plans = [
    {
      name: 'Essentials',
      price: '$45',
      subtext: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured'
      ],
      note: '(Govt. filing fee not included)',
      cta: 'Get Essential Plan'
    },
    {
      name: 'Enhanced',
      price: '$149',
      subtext: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter'
      ],
      note: '(Govt. filing fee not included)',
      cta: 'Get Enhanced Plan'
    },
    {
      name: 'Deluxe',
      price: '$299',
      subtext: '+ Federal Filing Fee',
      popular: true,
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager'
      ],
      note: '(Govt. filing fee not included)',
      cta: 'Get Deluxe Plan'
    },
    {
      name: 'Platinum',
      price: '$349',
      subtext: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager',
        'High Approval Rate',
        'Comprehensive Search Report',
        'High Priority Service',
        'Cease & Desist Letter'
      ],
      note: '(Govt. filing fee not included)',
      cta: 'Get Platinum Plan'
    }
  ];

  return (
    <>
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" data-testid="pricing-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Choose Your Plan</h2>
            <p className="text-xl text-gray-600">Comprehensive trademark protection for every budget</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {plans.map((plan, idx) => (
              <div
                key={idx}
                className={`bg-white rounded-2xl p-8 shadow-lg relative flex flex-col ${
                  plan.popular ? 'ring-4 ring-red-500 transform scale-105' : ''
                } card-hover`}
                data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Recommended
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center mb-2">
                    <span className="text-4xl font-bold gradient-text">{plan.price}</span>
                  </div>
                  <p className="text-sm text-gray-600">{plan.subtext}</p>
                </div>

                <div className="space-y-3 mb-6 flex-grow max-h-80 overflow-y-auto">
                  {plan.features.map((feature, featureIdx) => (
                    <div key={featureIdx} className="flex items-start space-x-2">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500 mb-6 text-center">{plan.note}</p>

                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className={`w-full py-3 rounded-full font-semibold mt-auto ${
                    plan.popular
                      ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white'
                      : 'bg-white text-red-600 border-2 border-red-600 hover:bg-red-50'
                  }`}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default PricingTable;